import type Controller from '@ember/controller';
import Route from '@ember/routing/route';
import type PublicTransition from '@ember/routing/transition';
import { service } from '@ember/service';

import { hashSettled } from 'rsvp';

import type { PopupData, Service } from 'webapp-types';
import { TemplateObjectType } from 'webapp/enums/enums';

import BoardRecord from 'webapp/models/board';
import type BoardLoopRecord from 'webapp/models/board-loop';
import type TemplateRecord from 'webapp/models/template';

// any popup can be open, the template preview has the template
type TemplatePreviewData = Partial<PopupData.Board.Preview.ForTemplate>;

export default class BoardsPublicTemplatesTemplateConnectRoute extends Route {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('router') declare routerService: Service.Router;

	override beforeModel(): Promise<unknown> {
		const promises: Record<string, Promise<unknown>> = {};

		promises.boardPublicTemplates = this.mmTemplatesService.requestBoardPublicTemplates();

		promises.boardLoopsPublicTemplates =
			this.mmTemplatesService.requestBoardLoopPublicTemplates();

		return hashSettled(promises);
	}

	override model() {
		return this.modelFor('boards.public-templates.template') as TemplateRecord;
	}

	override afterModel(model: TemplateRecord): void {
		const templateModel = this.mmTemplatesService.createModel(model);

		const afterUse = (record: BoardRecord | BoardLoopRecord) => {
			const isTemplatesPath = this.routerService.currentRouteName?.startsWith(
					'boards.public-templates.template',
				),
				isOnSameTemplate =
					record.originateFrom &&
					(this.popupService.activePopup?.data as TemplatePreviewData | undefined)
						?.template?.objectId === record.originateFrom;

			if (isTemplatesPath && isOnSameTemplate) {
				if (record instanceof BoardRecord) {
					const boardModel = this.mmBoardsService.createModel(record);
					this.navigationService.transitionToRoute('boards.index').then(() => {
						this.mmBoardsService.openPreview(boardModel, null);
					});
				} else {
					const boardLoopModel = this.mmBoardLoopsService.createModel(record);
					this.navigationService.transitionToRoute('boards.loops.index').then(() => {
						this.mmBoardLoopsService.openPreviewPopup(boardLoopModel);
					});
				}
			}
		};

		const onResolveModalClose = () => {
			// is template - reroute after closing
			this.navigationService.transitionToRoute('boards.public-templates.template', model);
		};

		if (model.objectType === TemplateObjectType.BOARD) {
			this.mmBoardsService.createBoardFromTemplate(
				templateModel,
				'public_template',
				afterUse,
				onResolveModalClose,
			);
		} else {
			this.mmBoardLoopsService.createBoardLoopFromTemplate(
				templateModel,
				'public_template_loop',
				afterUse,
				onResolveModalClose,
			);
		}
	}

	override setupController(
		controller: Controller,
		model: TemplateRecord,
		transition: PublicTransition,
	): void {
		super.setupController(controller, model, transition);
	}
}
